
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { UserLicenseTypeEnum } from "../../components/enums/userLicenseTypeEnum";
import { IAuthAccount } from "../../interfaces/IAuthTokenResultDto";
import { IUserProfile, IUserSettings } from "../../interfaces/IUserProfile";
import { UserState } from "../../types";
import { typedAction } from "../helpers";
import { RESET_USER_ACCOUNT, SET_USER_ACCOUNT, SET_USER_PROFILE, SET_USER_SETTINGS } from "./actionTypes";
import emailMapper from "../../services/EmailMapper";
import LZString = require("lz-string");
import { logger } from "../../../../utilities/logger";
import { apiGetCompressedEmailToMetadataMapping, apiSetEmailToMetadataMapping, apiUpdateUserSettings } from "../../services/ApiService";

const initialState: UserState = {
  licenseType: UserLicenseTypeEnum.Expired,
  oneDriveWebUrl: "",
  account: null,
  settings: {
    showRecent: true,
    showPinned: true,
    showShared: true,
  },
  tenantSaveOnSendEnabled: false,
  tenantAllowOpenInSharePoint: true,
  tenantEnablePII: false,
  tenantEnableApprovalHooks: false,
  isActivated: false,
  tenantOcrLanguage: "",
  tenantEnableRedaction: false
};

export const setUserProfile = (profile: IUserProfile) => {
  return typedAction(SET_USER_PROFILE, profile);
};

const setUserSettings = (settings: IUserSettings) => {
  return typedAction(SET_USER_SETTINGS, settings);
};

export const updateUserSettings = (accountId: string, settings: IUserSettings): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch) => {
  //make api call with settings to db
  apiUpdateUserSettings(settings, accountId);
  //dispatch the userSetter so that it is updated in global state
  dispatch(setUserSettings(settings));
};

export const getEmailToMetaDataMapping = async (accountId: string, tenantEmailToMetadataMapping: string) => {
  try {
    const compressedUserMailMapping = await apiGetCompressedEmailToMetadataMapping(accountId);
    if (compressedUserMailMapping) {
      const userMailMappings: string = LZString.decompressFromBase64(compressedUserMailMapping);
      emailMapper.setMapping(userMailMappings);
    } 
    else if (tenantEmailToMetadataMapping) {
      const tenantMailMappings: string = LZString.decompressFromBase64(tenantEmailToMetadataMapping);
      emailMapper.setMapping(tenantMailMappings);
    }
  } catch (error) {
    logger(error, "user.ts (getEmailToMetaDataMapping)");
  }
};

export const setEmailToMetaDataMapping = async (accountId: string, emailMapping: string) => {
  emailMapper.updateMappingLists();
  const compressedData = LZString.compressToBase64(emailMapping);
  await apiSetEmailToMetadataMapping(accountId, compressedData);
};

export const setUserAccount = (account: IAuthAccount) => {
  return typedAction(SET_USER_ACCOUNT, account);
};

export const resetUserAccount = () => {
  return typedAction(RESET_USER_ACCOUNT);
};

type UserAction = ReturnType<typeof setUserAccount | typeof resetUserAccount | typeof setUserProfile | typeof setUserSettings>;

export function userReducer(state = initialState, action: UserAction): UserState {
  switch (action.type) {
    // case SET_USER_LICENSE_TYPE:
    //   return { ...state, licenseType: action.payload };
    case SET_USER_ACCOUNT:
      return { ...state, oneDriveWebUrl: action.payload?.oneDriveWebUrl, account: action.payload };
    case RESET_USER_ACCOUNT:
      return { ...initialState };
    case SET_USER_PROFILE: //logging in -> get profile -> set in redux
      return {
        ...state,
        licenseType: action.payload.userLicenseType,
        settings: action.payload.settings,
        tenantSaveOnSendEnabled: action.payload.tenantSaveOnSendEnabled ?? false,
        tenantAllowOpenInSharePoint: action.payload.tenantAllowOpenInSharePoint ?? true,
        tenantEnablePII: action.payload.tenantEnablePII ?? false,
        tenantEnableApprovalHooks: action.payload.tenantEnableApprovalHooks ?? false,
        isActivated: action.payload.isActivated ?? false,
        tenantOcrLanguage: action.payload.tenantOcrLanguage,
        tenantEnableRedaction: action.payload.tenantEnableRedaction,
        isLoadedFromCache: action.payload.isLoadedFromCache ?? false
      };
    case SET_USER_SETTINGS: //when toggling between seeing pinned/recent/shared this is called. 
      return { ...state, settings: action.payload }
    default:
      return state;
  }
}
